// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { HceGender } from '../../../interfaces/hce/hce-gender-prediction';
import { HceView } from '../../../interfaces/hce/hce-reference';
import {
  RefsNameMapping,
  // SV Tab Data
  StatsSVData,
  SvChrOpts,
  SvPieChartData,
  SvPieChartDataItem,
  // SV Json Data
  SvEnhancedHsv,
  SvfRec,
  SvChrArray,
} from '../../../interfaces/hce/hce-stats';
import {
  StatsSvparam,
} from '../../../shared/constants/hce/hce-stats-constants';
import { HceStatsService } from '../hce-stats/hce-stats.service';
import { HceContig, HceRefGroupMap, HceRefGroup, BaseItem, OverlapIndex, ChrTypeKey, EdgeVal, NormSpan, NormalizedSvType } from '../../../interfaces/hce/hce-results-summary-view';

@Injectable({
  providedIn: 'root',
})
export class HceResultsSummaryViewService {
  constructor(
    private hceStatsService: HceStatsService,
  ) { }

  public edgeKb(edge: EdgeVal | undefined, which: 'start' | 'end'): number {
    if (edge == null) return 0;
    if (typeof edge === 'number') return edge;
    const pk = edge.pos_kb;
    return which === 'start' ? (pk?.min ?? edge.min ?? 0) : (pk?.max ?? edge.max ?? 0);
  }

  public stdPos(x: number, e: number, w: number): NormSpan {
    const ave = (x + e) / 2;
    const length = Math.max(Math.max(x, e) - Math.min(x, e), w);
    return { start: ave - length / 2, end: ave + length / 2, size: length };
  }

  public isOverlapEnough(baseSV: NormSpan, chkSV: NormSpan, rate01: number): boolean {
    if (chkSV.start > baseSV.end || chkSV.end < baseSV.start) return false;
    const overlap = Math.min(baseSV.end, chkSV.end) - Math.max(baseSV.start, chkSV.start);
    const basis = Math.min(baseSV.size, chkSV.size);
    return overlap > basis * rate01;
  }

  public normType(t?: string): NormalizedSvType {
    const s = (t ?? '').trim().toLowerCase();
    if (s.startsWith('ins') || s === 'insertion') return 'Insertion';
    if (s.startsWith('dup') || s === 'duplication') return 'Duplication';
    if (s.startsWith('del') || s === 'deletion') return 'Deletion';
    if (s.startsWith('inv') || s === 'inversion') return 'Inversion';
    if (s.startsWith('tra') || s.startsWith('trans') || s === 'translocation') return 'Translocation';
    return 'default';
  }

  // SV → BaseItem
  public toItemFromSv(
    sv: any,                 // HceSv
    group: string,           // sampleName
    id2chr: Record<string, string>
  ): BaseItem | null {
    const t = this.normType(sv?.type);
    if (t === 'Translocation') return null;

    const chrName = id2chr[String(sv.chr_id)];
    if (!chrName) return null;

    const x = this.edgeKb(sv?.region?.chr?.start, 'start');
    const e = this.edgeKb(sv?.region?.chr?.end, 'end');
    const ref = Math.abs(sv?.region?.chr?.length ?? 0);
    const con = Math.abs(sv?.region?.contig?.length ?? 0);
    const wMax = Math.max(ref, con);
    const wLen = Math.abs(sv?.length ?? (Math.max(x, e) - Math.min(x, e)));

    return {
      group,
      chr: chrName,
      type: t as NormalizedSvType,
      baseSpan: this.stdPos(x, e, wMax),
      chkSpan: this.stdPos(x, e, wLen),
      src: { kind: 'sv', index: sv.__index__ } // 呼び出し側で index を付与
    };
  }

  // Annotation → BaseItem
  public toItemFromAnno(
    a: any,                  // HceAnnotation
    group: string,           // filename
    validChrs: Set<string>
  ): BaseItem | null {
    const t = this.normType(a?.type as string);
    if (t === 'Translocation') return null;
    const chrName = a.chromosome;
    if (!chrName || !validChrs.has(chrName)) return null;

    const x = (a.start ?? 0) / 1000;
    const e = (a.end ?? 0) / 1000;
    const ref = Math.abs((a.sVRefSize ?? 0) / 1000);
    const con = Math.abs((a.sVConSize ?? 0) / 1000);
    const wMax = Math.max(ref, con);
    const wLen = Math.abs((a.length ?? ((a.end ?? 0) - (a.start ?? 0))) / 1000);

    return {
      group,
      chr: chrName,
      type: t as NormalizedSvType,
      baseSpan: this.stdPos(x, e, wMax),
      chkSpan: this.stdPos(x, e, wLen),
      src: { kind: 'anno', index: a.__index__, file: group }
    };
  }


  /** Index construction (only once) */
  public buildIndex(groups: Map<string, BaseItem[]>): OverlapIndex {
    const byChrType = new Map<ChrTypeKey, BaseItem[]>();
    for (const [, items] of groups) {
      for (const it of items) {
        const key = `${it.chr}::${it.type}`;
        const arr = byChrType.get(key) ?? [];
        arr.push(it);
        byChrType.set(key, arr);
      }
    }
    for (const [, list] of byChrType) list.sort((a, b) => a.chkSpan.start - b.chkSpan.start);

    return { groups, byChrType };
  }

  // Pre-calculate the 'maximum overlap rate with other groups' for each group
  // for the parent unique number label
  public precomputeMaxRatio(index: OverlapIndex): OverlapIndex {
    const maxRatioByGroup = new Map<string, Float64Array>();
    for (const [g, items] of index.groups) {
      const ratios = new Float64Array(items.length);
      for (let i = 0; i < items.length; i++) {
        const base = items[i];
        const pool = index.byChrType.get(`${base.chr}::${base.type}`) ?? [];
        // Left end of the candidate in binary search
        let lo = 0, hi = pool.length;
        const s0 = base.baseSpan.start, s1 = base.baseSpan.end;
        while (lo < hi) {
          const mid = (lo + hi) >>> 1;
          if (pool[mid].chkSpan.end < s0) lo = mid + 1; else hi = mid;
        }
        let maxR = 0;
        for (let j = lo; j < pool.length; j++) {
          const cand = pool[j];
          if (cand.group === g) continue;
          if (cand.chkSpan.start > s1) break;
          const overlap = Math.min(base.baseSpan.end, cand.chkSpan.end) - Math.max(s0, cand.chkSpan.start);
          if (overlap <= 0) continue;
          const basis = Math.min(base.baseSpan.size, cand.chkSpan.size);
          const r = overlap / basis;
          if (r > maxR) maxR = r;
        }
        ratios[i] = maxR;
      }
      maxRatioByGroup.set(g, ratios);
    }
    return { ...index, maxRatioByGroup };
  }

  /** Recalculation of unique numbers */
  public countUniques(index: OverlapIndex, overlapRate01: number): Record<string, number> {
    // Recalculation of unique numbers 
    // (if there is a light maxRatioByGroup when overlapRate is changed, use it; otherwise, follow by scanning online)
    const out: Record<string, number> = {};
    for (const [g, items] of index.groups) {
      const ratios = index.maxRatioByGroup?.get(g);
      if (ratios) {
        let unique = 0;
        for (let i = 0; i < items.length; i++) {
          if (!(ratios[i] > overlapRate01)) unique++;
        }
        out[g] = unique;
      } else {
        // Fallback: Online judgment
        let unique = 0;
        for (const base of items) {
          const pool = index.byChrType.get(`${base.chr}::${base.type}`) ?? [];
          let lo = 0, hi = pool.length;
          const s0 = base.baseSpan.start, s1 = base.baseSpan.end;
          while (lo < hi) { const mid = (lo + hi) >>> 1; if (pool[mid].chkSpan.end < s0) lo = mid + 1; else hi = mid; }
          let duplicated = false;
          for (let j = lo; j < pool.length; j++) {
            const cand = pool[j];
            if (cand.group === base.group) continue;
            if (cand.chkSpan.start > s1) break;
            if (this.isOverlapEnough(base.baseSpan, cand.chkSpan, overlapRate01)) { duplicated = true; break; }
          }
          if (!duplicated) unique++;
        }
        out[g] = unique;
      }
    }
    return out;
  }

  /** Determination of visibility/highlight (returns a result set to apply to children) */
  public evaluateVisibility(
    index: OverlapIndex,
    opts: { selectedGroup?: string | null; overlapRate01: number; hideShared?: boolean }
  ): {
    svVisible: Set<number>; svHighlight: Set<number>;
    annoVisible: Map<string, Set<number>>; annoHighlight: Map<string, Set<number>>;
  } {
    const { selectedGroup: selected, overlapRate01: rate01, hideShared } = opts;

    const svVisible = new Set<number>();
    const svHighlight = new Set<number>();
    const annoVisible = new Map<string, Set<number>>();
    const annoHighlight = new Map<string, Set<number>>();

    for (const [g, items] of index.groups) {
      for (const it of items) {
        if (it.src.kind === 'sv') svVisible.add(it.src.index);
        else {
          const set = annoVisible.get(g) ?? new Set<number>();
          set.add(it.src.index);
          annoVisible.set(g, set);
        }
      }
    }

    // Hiding 'shared' (hideShared flag) and finalizing highlights
    const groupNames = [...index.groups.keys()];
    const totalCount = groupNames.length;
    const allcheck = !selected;

    for (const g of groupNames) {
      for (const base of index.groups.get(g) ?? []) {
        const key = `${base.chr}::${base.type}`;
        const pool = index.byChrType.get(key) ?? [];
        // Leftmost candidate (binary search)
        let lo = 0, hi = pool.length;
        const s0 = base.baseSpan.start, s1 = base.baseSpan.end;
        while (lo < hi) { const mid = (lo + hi) >>> 1; if (pool[mid].chkSpan.end < s0) lo = mid + 1; else hi = mid; }

        const hitByGroup = new Set<string>([g]); // Always includes oneself
        for (let j = lo; j < pool.length; j++) {
          const cand = pool[j];
          if (cand.chkSpan.start > s1) break;
          if (cand.group === g) continue;
          if (this.isOverlapEnough(base.baseSpan, cand.chkSpan, rate01)) {
            hitByGroup.add(cand.group);
            if (allcheck ? hitByGroup.size === totalCount : hitByGroup.size >= 2) break;
          }
        }
        const shared = allcheck ? (hitByGroup.size === totalCount && totalCount > 1)
          : (hitByGroup.size > 1);

        // Highlight = 'Does not overlap with other groups'
        const isUnique = hitByGroup.size === 1;

        // visible / invisible
        if (hideShared && shared) {
          if (base.src.kind === 'sv') svVisible.delete(base.src.index);
          else annoVisible.get(g)?.delete(base.src.index);
        }

        // highlight
        if (isUnique) {
          if (base.src.kind === 'sv') svHighlight.add(base.src.index);
          else {
            const set = annoHighlight.get(g) ?? new Set<number>();
            set.add(base.src.index);
            annoHighlight.set(g, set);
          }
        }
      }
    }

    return { svVisible, svHighlight, annoVisible, annoHighlight };
  }

  public parseContigs(text: string): HceContig[] {
    const lines = text.replace(/\r\n?/g, '\n').trim().split('\n');
    if (lines.length === 0) return [];
    const splitLine = (line: string) => line.trim().split(/\t+| {2,}|\s{2,}/).filter(Boolean);
    const header = splitLine(lines[0]).map(h => h.trim());
    const colIdx = {
      Chromosome: header.findIndex(h => /^chromosome$/i.test(h)),
      Start: header.findIndex(h => /^start$/i.test(h)),
      End: header.findIndex(h => /^end$/i.test(h)),
    };
    const hasValidHeader = colIdx.Chromosome >= 0 && colIdx.Start >= 0 && colIdx.End >= 0;
    const startRow = hasValidHeader ? 1 : 0;
    const out: HceContig[] = [];
    for (let i = startRow; i < lines.length; i++) {
      const raw = lines[i].trim();
      if (!raw) continue;
      const parts = splitLine(raw);
      const [chrom, startStr, endStr] = hasValidHeader
        ? [parts[colIdx.Chromosome], parts[colIdx.Start], parts[colIdx.End]]
        : [parts[0], parts[1], parts[2]];
      if (chrom == null || startStr == null || endStr == null) continue;
      const Start = Number(startStr);
      const End = Number(endStr);
      if (!Number.isFinite(Start) || !Number.isFinite(End)) continue;
      out.push({ Chromosome: String(chrom), Start, End });
    }
    return out;
  }

  public getGenderLabel(gender?: HceGender | null): string {
    if (gender != null) {
      if (gender == HceGender.Male) return "(M)";
      if (gender == HceGender.Female) return "(F)";
      if (gender == HceGender.Unknown) return "(U)";
    }
    return "(-)";
  }

  public mkRefGrpList(references: HceView[], chrNmList: string[]): HceRefGroupMap {
    const ret: HceRefGroupMap = new Map<string, HceRefGroup[]>();
    for (const chr of chrNmList) ret.set(chr, []);

    // Grouping by chr
    const byChr = new Map<string, HceView[]>();
    for (const rec of references) {
      const arr = byChr.get(rec.title) ?? [];
      arr.push(rec);
      byChr.set(rec.title, arr);
    }

    // Process for each chr (in ascending order of start), grouped by p/q switching
    for (const chrNm of chrNmList) {
      const recs = (byChr.get(chrNm) ?? []).slice().sort((a, b) => parseFloat(a.start) - parseFloat(b.start));
      if (recs.length === 0) continue;

      const groups = ret.get(chrNm)!;
      let curName = '';
      let lastEnd = 0;

      for (let i = 0; i < recs.length; i++) {
        const aRec = recs[i];
        const tmpName = (aRec.name ?? '').substring(0, 1);
        if (i === 0) {
          // First record: Start a new group
          groups.push({ start: parseFloat(aRec.start), end: parseFloat(aRec.end) });
        } else if (i === recs.length - 1) {
          // Last record: Confirm the current group's end to this end
          const lastGrpIdx = groups.length - 1;
          groups[lastGrpIdx].end = parseFloat(aRec.end);
        } else if ((tmpName === 'p' && tmpName !== curName) || (tmpName === 'q' && tmpName !== curName)) {
          // Switching p/q: Close with the previous end and start a new group
          const lastGrpIdx = groups.length - 1;
          groups[lastGrpIdx].end = lastEnd;
          groups.push({ start: parseFloat(aRec.start), end: parseFloat(aRec.end) });
        }
        curName = tmpName;
        lastEnd = parseFloat(aRec.end);
      }
    }
    return ret;
  }

  public computeWorldKb(referenceGroupList: HceRefGroupMap): number {
    const allEnds = [...referenceGroupList.values()].flatMap(groups =>
      groups
        .map(g => g?.end)
        .filter((e): e is number => typeof e === 'number')
    );

    return allEnds.length ? Math.max(...allEnds) / 1000 : 0;
  }

  public makeGeneText(genes: string[]): string {
    const genesLen = genes.length;
    if (genesLen > 0) {
      let gene_text = ""
      genes.forEach((gene, index) => {
        if (index != 0) gene_text += ", ";
        gene_text += gene;
      });
      return gene_text;
    } else {
      return '';
    }
  }

  // Convert "#RRGGBB" to 0xRRGGBB
  public hexToPixiColor(hex: string): number | null {
    const m = /^#([0-9a-fA-F]{6})$/.exec(hex.trim());
    if (!m) return null;
    return Number("0x" + m[1]);
  }

  // Convert "rgb(r,g,b)" → 0xRRGGBB
  public rgbToPixiColor(rgb: string): number | null {
    const m = /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/.exec(rgb.trim());
    if (!m) return null;
    const r = Math.max(0, Math.min(255, parseInt(m[1], 10)));
    const g = Math.max(0, Math.min(255, parseInt(m[2], 10)));
    const b = Math.max(0, Math.min(255, parseInt(m[3], 10)));
    return (r << 16) + (g << 8) + b; // == 0xRRGGBB
  }

  // genes (Up to 20, line break at the 10th item)
  public collectGenes(genes: string[]): string {
    const genesLen = genes.length;
    if (genesLen > 0) {
      const limit = Math.min(20, genesLen);
      const buf: string[] = [];
      for (let i = 0; i < limit; i++) {
        const token =
          i === 19 && genesLen > 20
            ? `${genes![i]} and ${genesLen - 20} more`
            : `${genes![i]}${i < limit - 1 ? ";" : ""}`;
        buf.push(token);
        if (i === 9) buf.push("\n");
      }
      return `\rGenes:${buf.join("")}`;
    } else {
      return '';
    }
  }

  public objectFindValue<K extends string, V>(obj: Record<K, V>, predicate: (entry: [K, V], index: number) => boolean): V | undefined {
    const e = Object.entries(obj).find((e, i) => predicate(e as [K, V], i)) as
      | [K, V]
      | undefined;
    return e?.[1];
  }

  public getMaxBpFromReference(referenceGroupList: HceRefGroupMap): number {
    let maxBp = 0;
    for (const [, groups] of referenceGroupList) {
      if (!groups || groups.length === 0) continue;
      const end = groups[groups.length - 1].end;
      if (end > maxBp) maxBp = end;
    }
    return maxBp;
  }

  public ceilToUnit(n: number, unit = 10000) {
    if (!Number.isFinite(n) || unit <= 0) return NaN;
    return Math.ceil(n / unit) * unit;
  }

  /**
   * get sample SV data
   * (stats.php getStatsSampleSVData)
   */
  public getStatsSampleSVData(svJson: SvEnhancedHsv, mappedRefNameTable: RefsNameMapping): StatsSVData {
    let smartydata: StatsSVData = {
      errorMessage: []
    };

    if (!svJson || !mappedRefNameTable) {
      smartydata.errorMessage = ['makeStatsCoveragetData failed. empty jsonData'];
      return smartydata;
    }

    let { svfRec, svversion, errorMessage } = this.hceStatsService.getSvDatalist(svJson, mappedRefNameTable, false);
    if (svfRec.length === 0) {
      // unuse TSV rep
      smartydata.errorMessage = [...errorMessage, "Not found SV data."];
      return smartydata;
    }

    // Make data 
    let svchrdata: SvChrArray = this.initChrArray(mappedRefNameTable);  // key:noOfChr, val:Refname
    this.mkPieChartData(svfRec, svchrdata, mappedRefNameTable);
    // pie chart data (Order desc)
    smartydata.chrOpts = this.sortPieChartData(svchrdata, mappedRefNameTable);

    let pieChartData: SvPieChartData = {};
    for (const [chr, lists] of Object.entries(svchrdata)) {
      pieChartData[chr] = {
        values: [],
        total: 0,
        seriesColors: [],
      } as SvPieChartDataItem;

      for (const [type, val] of Object.entries(lists)) {
        if (type == StatsSvparam.PIE_CHART_VALUE_TOTAL) {
          pieChartData[chr].total = val;
        }
        else if (type == StatsSvparam.PIE_CHART_COLORS) {
          pieChartData[chr].seriesColors = val;
        }
        else {
          pieChartData[chr].values.push([type + "(" + val + ")", val])

        }
      }
    }

    smartydata.pieChartData = pieChartData;
    smartydata.success = true;
    return smartydata;
  }


  /**
   * Format SV data for a pie chart
     * (stats.php mkPieChartData)
   */
  private mkPieChartData(fRec: SvfRec, refRet: Record<string, Record<string, number>>, mappedRefNameTable: RefsNameMapping) {
    refRet[0] = {};
    refRet[0][StatsSvparam.PIE_CHART_VALUE_TOTAL] = 0;
    const hasKey = (key: number): boolean => {
      return String(key) in mappedRefNameTable;
    };

    for (const [key, aRec] of Object.entries(fRec)) {
      const chr = hasKey(Number(aRec.noOfChr)) ? aRec.noOfChr : aRec.noOfChr2;
      if (chr === undefined) continue;
      const type = aRec.type + 's';

      // Initial value
      refRet[chr] ??= {}; // If `chr` is not present, initialize the array
      refRet[chr][type] ??= 0; // Add `type: 0` if the type field does not exist
      refRet[chr][StatsSvparam.PIE_CHART_VALUE_TOTAL] ??= 0; // Add `PIE_CHART_VALUE_TOTAL: 0` if the type field does not exist
      refRet[0][type] ??= 0; // If `type` is missing in ALL(0), add it with value 0

      // increment
      refRet[chr][type] += 1;
      refRet[chr][StatsSvparam.PIE_CHART_VALUE_TOTAL] += 1;
      refRet[0][type] += 1;
      refRet[0][StatsSvparam.PIE_CHART_VALUE_TOTAL] += 1;
    }

    for (const k of Object.keys(refRet)) {
      refRet[k] = Object.fromEntries(
        Object.entries(refRet[k]).sort(([, a], [, b]) => (b as number) - (a as number))
      );
    }
  }

  /**
   * Sort pie chart data in descending order by value and add the color list for each item
     * (stats.php sortPieChartData)
   */
  private sortPieChartData(ret: Record<string, Record<string, any>>, mappedRefNameTable: RefsNameMapping): SvChrOpts {
    let chrOpts: SvChrOpts = {};

    for (const [aChr, aChrList] of Object.entries(ret)) {
      if (Object.keys(aChrList).length < 1) {
        delete ret[aChr];
        continue;
      }

      let label;
      if (Number(aChr) === 0) {
        label = 'ALL';
      } else {
        label = mappedRefNameTable[aChr];
      }

      chrOpts[aChr] = label;
      ret[aChr][StatsSvparam.PIE_CHART_COLORS] = [];

      for (const [type, val] of Object.entries(ret[aChr])) {
        let typeColor: string | undefined;
        switch (type) {
          case StatsSvparam.TYPE_SV_DELETION + 's':
            typeColor = StatsSvparam.COL_SV_DELETION;
            break;
          case StatsSvparam.TYPE_SV_INSERTION + 's':
            typeColor = StatsSvparam.COL_SV_INSERTION;
            break;
          case StatsSvparam.TYPE_SV_INVERSION + 's':
            typeColor = StatsSvparam.COL_SV_INVERSION;
            break;
          case StatsSvparam.TYPE_SV_TRANSLOCATION + 's':
            typeColor = StatsSvparam.COL_SV_TRANSLOCATION;
            break;
          case StatsSvparam.TYPE_SV_DUPLICATION + 's':
            typeColor = StatsSvparam.COL_SV_DUPLICATION;
            break;
          default:
            if (type != StatsSvparam.PIE_CHART_VALUE_TOTAL && type != StatsSvparam.PIE_CHART_COLORS) {
              typeColor = StatsSvparam.COL_SV_OTHER;
            }
        }
        if (typeColor) {
          ret[aChr][StatsSvparam.PIE_CHART_COLORS].push(typeColor);
        }
      }
    }
    return chrOpts;
  }

  /**
   * Create an empty array with the same keys as the Mapping Table
   * (stats.php _initChrArray)
   */
  private initChrArray(chrNmList: RefsNameMapping): SvChrArray {
    const ret: Record<string, Record<string, number>> = {};
    for (const chr of Object.keys(chrNmList)) {
      ret[chr] = {};
    }
    return ret;
  }
}